import {lazy, useCallback, useContext, useEffect, useState} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {AxiosError} from 'axios';
import {format} from 'date-fns';
import {ToastType} from 'patient-ping-remedy/packages/toast';
import Button, {StyleType} from 'patient-ping-remedy/packages/button';
import IconButton, {IconButtonSize} from 'patient-ping-remedy/packages/icon-button';
import {ICONS} from 'patient-ping-remedy/packages/icon';
import Input from 'patient-ping-remedy/packages/input';
import DropdownList from 'patient-ping-remedy/packages/dropdown-list';
import {Value} from 'patient-ping-remedy/packages/dropdown-list/lib/DropdownList';
import DatePicker from 'patient-ping-remedy/packages/date-picker';
import Banner, {BannerTypes} from 'patient-ping-remedy/packages/banner';
import DismissibleBanner from '@common/DismissibleBanner';
import {DatePickerWrapper} from '@common/DatePickerWrapper';
import DisplayHelpers from '@/helpers/display_helpers';
import {state_codes} from '@/helpers/state_codes';
import {
  CareJourneyDetailsResponse,
  CareJourneyStep,
  CareJourneyReviewProvider,
} from '@/api/dto/care-journey';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useActiveRoster, useRosterStore} from '@/store/roster_store';
import {useAuthStore} from '@/store/auth_store';
import {usePreReviewDraftStore} from '@/store/pre_review_draft_store';
import RemoteComponent from '@common/RemoteComponent';
import InfoIconTooltip from '@common/InfoIconTooltip';
import {RegistryUnitSelection, RegistryPractitionerSelection} from '@/api/dto/thoth';
import {useAlertStore} from '@/store/alert_store';
import Helpers from '@/helpers/helpers';
import {GENERICERROR} from '@manual_encounters/constants/BannerMessages';
import {
  PreReviewStringField,
  CLIENT_PROFILE_REQUIRED,
  PRE_REVIEW_GENDER_OPTIONS,
  clientProfileErrors,
  reviewReferralErrors,
  getUnmetConfirmConditions,
  toConfirmRequest,
  toReviewDataEditRequest,
} from '@/components/pages/tasks/task_edit/progress/pre_review/preReviewHelpers';
import {
  PRE_REVIEW_STEP_COUNT,
  PRE_REVIEW_STEP_LABELS,
  usePreReviewWizard,
} from '@/components/pages/tasks/task_edit/progress/pre_review/PreReviewWizardContext';
import {
  Card,
  CardBody,
  Content,
  ContentHeader,
  EmptyState,
  FieldGrid,
  FullRow,
  NavButtons,
  SectionTitle,
  RequiredMark,
  ErrorText,
  SummaryBlock,
  Textarea,
  UnmetList,
} from '@/components/pages/tasks/task_edit/progress/pre_review/CareJourneyPreReview.styles';

// Orange's Registry pickers, federated from the thoth remote (CARECO-5762 / ORANGE-28592). Each is
// the whole section (empty state, Select button, selection modal, selected card); careco-ui mounts
// them and owns only `value` + `onChange`. Graceful-unavailable is handled by RemoteComponent + the
// mf-plugin-fallback runtime plugin.
// PracticeSelect drives the required Referring Practice section (persisted as practiceId/practiceName).
const RegistryPracticeSelect = lazy(() => import('thoth/PracticeSelect'));
// ProviderSelect drives the optional PCP section (persisted as pcpPractitionerId/pcpNpi/pcpName).
const RegistryProviderSelect = lazy(() => import('thoth/ProviderSelect'));

type Props = {
  careJourneyStep: CareJourneyStep;
  careJourneyDetails: CareJourneyDetailsResponse;
  disabled: boolean;
};

// Stable ids so an instruction banner's dismissal is keyed to the step, not the remounted element.
const CLIENT_INFO_BANNER_ID = 'client-info-confirm';
const REFERRAL_INFO_BANNER_ID = 'referral-info-confirm';

function providerName(provider?: CareJourneyReviewProvider): string {
  if (!provider) return '';
  return [provider.firstName, provider.lastName].filter(Boolean).join(' ');
}

// US-state select options, sourced from the shared state_codes constant (same source the
// event-consumer state filter uses) rather than a new hardcoded list.
const stateOptions: Value[] = state_codes.map(code => ({value: code.value, label: code.label}));

// Code → word for display; form.gender stays the code, so the summary mirrors the dropdown label.
const genderLabel = (code: string): string =>
  PRE_REVIEW_GENDER_OPTIONS.find(option => option.value === code)?.label ?? '';

export default function CareJourneyPreReview({careJourneyStep, careJourneyDetails, disabled}: Props) {
  const {carecoApi} = useContext(CarecoApiContext);
  const currentRoster = useActiveRoster();
  const authStore = useAuthStore();
  const rosterStore = useRosterStore();
  const {addAlert} = useAlertStore();
  const queryClient = useQueryClient();
  const {setDraftAction, clearDraftAction} = usePreReviewDraftStore();
  const {stepIndex, setStepIndex, setStepGuard, dismissedBanners, dismissBanner, form, setForm} = usePreReviewWizard();

  const reviewData = careJourneyStep.reviewData;
  const referringProvider = reviewData?.journey?.referringProvider;

  const [showClientErrors, setShowClientErrors] = useState(false);
  const [showReviewErrors, setShowReviewErrors] = useState(false);

  const isReadOnly = disabled
    || careJourneyStep.status === 'completed'
    || !careJourneyStep.taskId;

  const errors = showClientErrors ? clientProfileErrors(form) : {};
  const reviewErrors = showReviewErrors ? reviewReferralErrors(form) : {};
  const unmetConfirmConditions = getUnmetConfirmConditions(form, reviewData);
  const confirmReady = unmetConfirmConditions.length === 0;

  const {mutate: confirm, isPending} = useMutation({
    mutationFn: async () =>
      carecoApi?.confirmPreReview(
        careJourneyDetails.careJourneyInstanceId,
        currentRoster!.id,
        toConfirmRequest(form, careJourneyStep.taskId!),
      ),
    onSuccess() {
      addAlert({type: ToastType.SUCCESS, content: 'Care journey created from referral.'});
      void queryClient.refetchQueries({
        queryKey: ['careJourneyDetails', careJourneyDetails.careJourneyInstanceId],
      });
    },
    onError(error: AxiosError) {
      Helpers.alertOnError(`${GENERICERROR} ${Helpers.traceId(error)}`, error);
    },
  });

  const {mutate: saveDraft, isPending: isSavingDraft} = useMutation({
    mutationFn: async () =>
      carecoApi?.saveReviewData(
        careJourneyDetails.careJourneyInstanceId,
        currentRoster!.id,
        toReviewDataEditRequest(form),
      ),
    onSuccess() {
      addAlert({type: ToastType.SUCCESS, content: 'Draft saved.'});
      void queryClient.refetchQueries({
        queryKey: ['careJourneyDetails', careJourneyDetails.careJourneyInstanceId],
      });
    },
    onError(error: AxiosError) {
      Helpers.alertOnError(`${GENERICERROR} ${Helpers.traceId(error)}`, error);
    },
  });

  const update = (key: PreReviewStringField) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm(prev => ({...prev, [key]: event.target.value}));

  const setFieldValue = (key: PreReviewStringField, value: string) =>
    setForm(prev => ({...prev, [key]: value}));

  // Rehydrate the picker from the persisted slim fields (unitId=practiceId, displayName=practiceName)
  // so a saved draft reopens with the practice shown; the module renders a compact name-only panel
  // until it is re-picked (CARECO-5762).
  const practiceValue: RegistryUnitSelection | null = form.practiceId
    ? {schemaVersion: 1, unitId: form.practiceId, facilityId: null, displayName: form.practiceName, tier: null}
    : null;

  // The module's confirmed selection (or null on clear) is persisted into the review form's practice
  // fields, which flow through toConfirmRequest / toReviewDataEditRequest unchanged.
  const handlePracticeChange = (selection: RegistryUnitSelection | null) =>
    setForm(prev => ({
      ...prev,
      practiceId: selection?.unitId ?? '',
      practiceName: selection?.displayName ?? '',
    }));

  // Rehydrate the PCP picker from the persisted slim fields: practitionerId is the key,
  // displayName=pcpName, npi=pcpNpi (nullable). The richer snapshot (title/phone/email/locations) is
  // not persisted, so a reopened draft shows a name-only card until the provider is re-picked
  // (CARECO-5762 / ORANGE-28592).
  const providerValue: RegistryPractitionerSelection | null = form.pcpPractitionerId
    ? {
      schemaVersion: 1,
      practitionerId: form.pcpPractitionerId,
      npi: form.pcpNpi || null,
      displayName: form.pcpName,
      title: null,
      phone: null,
      email: null,
      locations: [],
    }
    : null;

  // The module's confirmed provider (or null on remove) is persisted into the review form's PCP
  // fields, which flow through toConfirmRequest / toReviewDataEditRequest unchanged.
  const handleProviderChange = (selection: RegistryPractitionerSelection | null) =>
    setForm(prev => ({
      ...prev,
      pcpPractitionerId: selection?.practitionerId ?? '',
      pcpNpi: selection?.npi ?? '',
      pcpName: selection?.displayName ?? '',
    }));

  const isRequired = (field: PreReviewStringField): boolean =>
    CLIENT_PROFILE_REQUIRED.includes(field);

  const requiredFlag = (field: PreReviewStringField): 'required' | '' =>
    isRequired(field) ? 'required' : '';

  const textField = (label: string, key: PreReviewStringField, readOnly = false) => (
    <Input
      id={`pre-review-${key}`}
      labelText={label}
      value={form[key]}
      onChange={update(key)}
      isDisabled={isReadOnly || readOnly}
      required={requiredFlag(key)}
      error={Boolean(errors[key])}
      errorMessage={errors[key]}
    />
  );

  // Standard date-picker control; stored value stays a `yyyy-MM-dd` string so validation
  // (clientProfileErrors) and toConfirmRequest keep seeing the prefill format.
  const dateField = (label: string, key: PreReviewStringField) => (
    <DatePickerWrapper>
      <DatePicker
        id={`pre-review-${key}`}
        labelText={label}
        value={DisplayHelpers.normalizeDateValue(form[key])}
        onChange={(date: Date | null) => {
          setFieldValue(key, date ? format(date, 'yyyy-MM-dd') : '');
          return date;
        }}
        maxDate={new Date()}
        disabled={isReadOnly}
        required={requiredFlag(key)}
        externalError={Boolean(errors[key])}
        externalErrorText={errors[key]}
      />
    </DatePickerWrapper>
  );

  // Standard dropdown/select control; stored value stays the option `value` string.
  const selectField = (label: string, key: PreReviewStringField, items: Value[]) => (
    <DropdownList
      id={`pre-review-${key}`}
      label={label}
      items={items}
      value={items.find(item => item.value === form[key])}
      handleChange={(newValue: Value | null) => setFieldValue(key, newValue?.value ?? '')}
      disabled={isReadOnly}
      required={isRequired(key)}
      error={Boolean(errors[key])}
      errorMessage={errors[key]}
    />
  );

  // Standard phone control; display is formatted while the stored value stays raw digits.
  const phoneField = (label: string, key: PreReviewStringField) => (
    <Input
      id={`pre-review-${key}`}
      labelText={label}
      value={DisplayHelpers.formatPhoneNumber(form[key])}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
        setFieldValue(key, event.target.value.replace(/\D/g, '').substring(0, 10))}
      placeholder="(___) ___-____"
      isDisabled={isReadOnly}
      required={requiredFlag(key)}
      error={Boolean(errors[key])}
      errorMessage={errors[key]}
    />
  );

  // Per-step forward-navigation gate. Backward moves are always allowed; advancing past the
  // client-profile step (0) requires a valid profile, and advancing to Create (step 2) requires a
  // valid referral — keyed on the target so a direct 0 -> 2 jump still validates the referral.
  // Shared by the Next button and the progress rail so rail clicks can't skip validation or mark an
  // invalid step Completed (CARECO-6080).
  const canNavigateToStep = useCallback((target: number): boolean => {
    if (target <= stepIndex) return true;
    if (stepIndex === 0 && Object.keys(clientProfileErrors(form)).length > 0) {
      setShowClientErrors(true);
      return false;
    }
    if (target >= 2 && Object.keys(reviewReferralErrors(form)).length > 0) {
      setShowReviewErrors(true);
      // Land on Review Referral so a blocked jump (e.g. a direct 0 -> 2) shows the referral error
      // instead of silently staying on an earlier step with no visible feedback (CARECO-6167).
      setStepIndex(1);
      return false;
    }
    return true;
  }, [stepIndex, form, setStepIndex]);

  // Expose the guard to the host rail (TaskEditProgress) so forward rail navigation is validated too.
  // Cleared on unmount so the rail falls back to its prefill-based baseline on the Details view
  // (CARECO-6167).
  useEffect(() => {
    setStepGuard(canNavigateToStep);
    return () => setStepGuard(null);
  }, [canNavigateToStep, setStepGuard]);

  // Publish the Save draft action to the task header (CARECO-6282): the header renders it beside
  // Reject Referral via usePreReviewDraftStore. Mirrors the previous in-content button's disabled
  // logic; cleared on unmount so the header action disappears when leaving the pre-review step.
  useEffect(() => {
    setDraftAction({
      save: () => saveDraft(),
      isSaving: isSavingDraft,
      disabled: isReadOnly || isSavingDraft || isPending || !currentRoster,
    });
    return () => clearDraftAction();
  }, [setDraftAction, clearDraftAction, saveDraft, isSavingDraft, isPending, isReadOnly, currentRoster]);

  const handleNext = () => {
    if (canNavigateToStep(stepIndex + 1)) {
      setStepIndex(Math.min(stepIndex + 1, PRE_REVIEW_STEP_COUNT - 1));
    }
  };

  return (
    <Content aria-label="Provider referral pre-initiation review">
      <ContentHeader>
        <h2>{PRE_REVIEW_STEP_LABELS[stepIndex]}</h2>
        <NavButtons>
          {stepIndex > 0 && (
            <Button styleType={StyleType.SECONDARY} onClick={() => setStepIndex(stepIndex - 1)}>
              Back
            </Button>
          )}
          {/* Save draft (CARECO-5760) now lives in the task header beside Reject Referral
              (CARECO-6282); see PreReviewSaveDraftHeaderAction + usePreReviewDraftStore. */}
          {stepIndex < PRE_REVIEW_STEP_COUNT - 1 && (
            <Button onClick={handleNext}>Next</Button>
          )}
          {stepIndex === PRE_REVIEW_STEP_COUNT - 1 && (
            <Button
              onClick={() => confirm()}
              disabled={isReadOnly || isPending || isSavingDraft || !currentRoster || !confirmReady}
            >
              Create
            </Button>
          )}
        </NavButtons>
      </ContentHeader>

      {stepIndex === 0 && (
        <>
          <DismissibleBanner
            dismissed={dismissedBanners.has(CLIENT_INFO_BANNER_ID)}
            onDismiss={() => dismissBanner(CLIENT_INFO_BANNER_ID)}
          >
            Please confirm this client information matches the original referral.
          </DismissibleBanner>
          <SectionTitle>Basic Information</SectionTitle>
          <FieldGrid>
            {textField('First Name', 'firstName')}
            {textField('Middle Name', 'middleName')}
            {textField('Last Name', 'lastName')}
            {dateField('Date of Birth', 'dob')}
            {selectField('Gender', 'gender', PRE_REVIEW_GENDER_OPTIONS)}
            {/* Read-only: the Confirm endpoint (ConfirmPreReviewRequest) has no client-id field,
                so edits here cannot be submitted (CARECO-6167). */}
            {textField('Client ID', 'clientId', true)}
          </FieldGrid>
          <SectionTitle>Address</SectionTitle>
          <FieldGrid>
            <FullRow>{textField('Street 1', 'addressLine1')}</FullRow>
            <FullRow>{textField('Street 2', 'addressLine2')}</FullRow>
            {textField('City', 'addressCity')}
            {selectField('State', 'addressState', stateOptions)}
            {textField('Zip Code', 'addressZip')}
          </FieldGrid>
          <SectionTitle>Phone number</SectionTitle>
          <FieldGrid>
            {phoneField('Home phone number', 'phoneHome')}
            {phoneField('Cellphone number', 'phoneMobile')}
          </FieldGrid>
        </>
      )}

      {stepIndex === 1 && (
        <>
          <DismissibleBanner
            dismissed={dismissedBanners.has(REFERRAL_INFO_BANNER_ID)}
            onDismiss={() => dismissBanner(REFERRAL_INFO_BANNER_ID)}
          >
            Please confirm this referral information matches the original referral.
          </DismissibleBanner>
          {/* Referring Practice is the required Registry pick (CARECO-5762 / ORANGE-28592): the
              navigator selects it from the Network Registry. PracticeSelect is the whole section
              (empty state + Select + modal + selected card); careco-ui owns only value/onChange and
              persists it as the form's practiceId/practiceName. */}
          <SectionTitle>Referring Practice <RequiredMark>*</RequiredMark></SectionTitle>
          {isReadOnly ? (
            form.practiceName ? (
              <Card>
                <CardBody>
                  <strong>{form.practiceName}</strong>
                </CardBody>
              </Card>
            ) : (
              <EmptyState>No practice selected.</EmptyState>
            )
          ) : currentRoster ? (
            // accessToken is injected by RemoteComponent; facilityId is the roster uuid (sets the
            // network scope), mirroring the thoth/CareJourneyNetworkBrowser mount. thoth reads its
            // permission roster from the store's selectedRoster ?? currentRoster — NOT taskRoster —
            // so on a task page it would otherwise check the facility-picker roster and deny
            // non-Bamboo users. Override selectedRoster in the passed store with the active (task)
            // roster so the permission check and facility resolve to the same roster, matching the
            // working Referrals MF (CARECO-6280 / CARECO-6293).
            <RemoteComponent
              remote="thoth"
              component={RegistryPracticeSelect}
              authStore={authStore}
              rosterStore={{...rosterStore, selectedRoster: currentRoster}}
              facilityId={currentRoster.uuid}
              value={practiceValue}
              onChange={handlePracticeChange}
              label=""
              required
              emptyTitle="No practice selected"
              emptyDescription="Choose a practice from the Network Registry"
            />
          ) : (
            <EmptyState>Select a roster to choose a practice.</EmptyState>
          )}

          <SectionTitle>Referring Provider</SectionTitle>
          {referringProvider && !form.referringProviderRemoved ? (
            <Card>
              <CardBody>
                <strong>{providerName(referringProvider)}</strong>
                <span>{referringProvider.type}</span>
                <span>{referringProvider.phone}</span>
                <span>{referringProvider.email}</span>
              </CardBody>
              {/* CARECO-6285: remove the referring provider from the record. Sets the form flag;
                  persisted on Save draft / Create via removeReferringProvider, which clears the
                  ingested blob backend-side (hard removal, no undo). */}
              <IconButton
                iconClass={ICONS.times}
                styleType={StyleType.TERTIARY}
                size={IconButtonSize.SMALL}
                aria-label="Remove referring provider"
                disabled={isReadOnly}
                onClick={() => setForm(prev => ({...prev, referringProviderRemoved: true}))}
              />
            </Card>
          ) : (
            <EmptyState>No referring provider on the referral.</EmptyState>
          )}

          {/* PCP is optional (Decision #16): ProviderSelect is the whole section (empty state +
              Select + modal + selected card); careco-ui owns only value/onChange and persists the
              pick as pcpPractitionerId (key) + pcpNpi (nullable snapshot) + pcpName. initialQuery
              seeds the Registry search with the referring provider's name (ORANGE-28592). */}
          <SectionTitle>
            Primary Care Provider (PCP)
            <InfoIconTooltip
              style={{marginLeft: '8px'}}
              text={
                'If the referring provider is also the member\u2019s primary care provider, '
                + 'you can select them here as the PCP.'
              }
            />
          </SectionTitle>
          {isReadOnly ? (
            form.pcpName ? (
              <Card>
                <CardBody>
                  <strong>{form.pcpName}</strong>
                </CardBody>
              </Card>
            ) : (
              <EmptyState>No PCP selected.</EmptyState>
            )
          ) : currentRoster ? (
            // Override selectedRoster in the passed store with the active (task) roster so thoth's
            // permission check lands on the task roster, not the facility-picker roster it reads off
            // the store by default (CARECO-6280/6281/6293).
            <RemoteComponent
              remote="thoth"
              component={RegistryProviderSelect}
              authStore={authStore}
              rosterStore={{...rosterStore, selectedRoster: currentRoster}}
              facilityId={currentRoster.uuid}
              value={providerValue}
              onChange={handleProviderChange}
              emptyTitle="No PCP selected"
              emptyHint="Choose a provider from the Network Registry"
              initialQuery={{name: providerName(referringProvider)}}
            />
          ) : (
            <EmptyState>Select a roster to choose a provider.</EmptyState>
          )}

          <SectionTitle>Reason for referral <RequiredMark>*</RequiredMark></SectionTitle>
          <Textarea
            aria-label="Reason for referral"
            value={form.reasonForReferral}
            onChange={update('reasonForReferral')}
            disabled={isReadOnly}
          />
          {reviewErrors.reasonForReferral && (
            <ErrorText>{reviewErrors.reasonForReferral}</ErrorText>
          )}
        </>
      )}

      {stepIndex === 2 && (
        <>
          <SectionTitle>Client Profile</SectionTitle>
          <SummaryBlock>
            <span>
              {[form.firstName, form.middleName, form.lastName].filter(Boolean).join(' ')}
            </span>
            <span>DOB: {form.dob || '-'}</span>
            <span>Gender: {genderLabel(form.gender) || '-'}</span>
            <span>Client ID: {form.clientId || '-'}</span>
            <span>
              Address: {[form.addressLine1, form.addressCity, form.addressState, form.addressZip]
                .filter(Boolean).join(', ') || '-'}
            </span>
            <span>Home phone number: {form.phoneHome || '-'}</span>
            <span>Cellphone number: {form.phoneMobile || '-'}</span>
          </SummaryBlock>

          <SectionTitle>Referring Practice</SectionTitle>
          <SummaryBlock>
            <strong>{form.practiceName || '-'}</strong>
          </SummaryBlock>

          <SectionTitle>Referring Provider</SectionTitle>
          <SummaryBlock>
            {form.referringProviderRemoved ? (
              <strong>-</strong>
            ) : (
              <>
                <strong>{providerName(referringProvider) || '-'}</strong>
                <span>{referringProvider?.type}</span>
                <span>{referringProvider?.phone}</span>
                <span>{referringProvider?.email}</span>
              </>
            )}
          </SummaryBlock>

          <SectionTitle>Primary Care Provider (PCP)</SectionTitle>
          <SummaryBlock>
            <strong>{form.pcpName || '-'}</strong>
          </SummaryBlock>

          <SectionTitle>Reason for referral</SectionTitle>
          <SummaryBlock>
            <span>{form.reasonForReferral || '-'}</span>
          </SummaryBlock>

          {!confirmReady && (
            <Banner type={BannerTypes.WARNING}>
              <span>Complete the following before creating the care journey:</span>
              <UnmetList>
                {unmetConfirmConditions.map(condition => (
                  <li key={condition.key}>{condition.label}</li>
                ))}
              </UnmetList>
            </Banner>
          )}
        </>
      )}
    </Content>
  );
}
